import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Project } from '../types';

interface Props {
  project: Project & { creator_name?: string; creator_email?: string; creator_role?: string };
  onClose: () => void;
  onEdit: () => void;
}

interface Stats {
  open: number;
  inProgress: number;
  closed: number;
  total: number;
  members: number;
}

const PALETTE = ['#6366f1','#f59e0b','#10b981','#3b82f6','#ec4899','#8b5cf6','#14b8a6','#f97316'];
const ROLE_COLORS: Record<string, string> = { admin: '#1C9B82', developer: '#3b82f6', tester: '#f59e0b' };

function cardColor(name: string) {
  return PALETTE[name.charCodeAt(0) % PALETTE.length];
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function safeDomain(url: string) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

export default function ProjectDetailsModal({ project, onClose, onEdit }: Props) {
  const { role, profile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);

  const canManage = role === 'admin' || profile?.user_id === project.created_by;
  const color     = cardColor(project.name);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    Promise.all([
      api.get<{ status: string }[]>(`/api/issues?project_id=${project.id}`),
      api.get<{ user_id: string }[]>(`/api/projects/${project.id}/members`),
    ]).then(([issues, members]) => {
      setStats({
        open:       issues.filter(r => r.status === 'Open').length,
        inProgress: issues.filter(r => r.status === 'In Progress').length,
        closed:     issues.filter(r => r.status === 'Closed').length,
        total:      issues.length,
        members:    members.length,
      });
    }).catch(() => {});
  }, [project.id]);

  const creatorName    = project.creator_name?.trim() || project.creator_email?.split('@')[0] || 'Unknown';
  const creatorRole    = project.creator_role ?? '';
  const creatorInitial = creatorName.charAt(0).toUpperCase();

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel project-details-modal" role="dialog" aria-modal="true" aria-labelledby="pd-title">

        {/* Hero header */}
        <div className="pd-hero" style={{ background: `linear-gradient(135deg, ${color}22 0%, ${color}08 100%)`, borderBottom: `3px solid ${color}` }}>
          <div className="pd-hero-content">
            <div className="pd-avatar" style={{ background: color }}>
              {project.name.charAt(0).toUpperCase()}
            </div>
            <div className="pd-hero-meta">
              <h2 id="pd-title" className="pd-name">{project.name}</h2>
              <div className="pd-badges">
                {project.visibility === 'private' ? (
                  <span className="project-private-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                    Private
                  </span>
                ) : (
                  <span className="project-public-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="10" height="10">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
                    </svg>
                    Public
                  </span>
                )}
                {project.status === 'archived' && <span className="project-archived-badge">Archived</span>}
              </div>
            </div>
          </div>
          <button className="modal-close-btn pd-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="pd-body">

          {/* Stats */}
          <div className="pd-stats">
            {stats === null ? (
              <div className="pd-stats-loading"><span className="btn-spinner" /></div>
            ) : (
              <>
                <div className="pd-stat">
                  <div className="pd-stat-value" style={{ color: 'var(--color-open)' }}>{stats.open}</div>
                  <div className="pd-stat-label">Open</div>
                </div>
                <div className="pd-stat-divider" />
                <div className="pd-stat">
                  <div className="pd-stat-value" style={{ color: 'var(--color-progress)' }}>{stats.inProgress}</div>
                  <div className="pd-stat-label">In Progress</div>
                </div>
                <div className="pd-stat-divider" />
                <div className="pd-stat">
                  <div className="pd-stat-value" style={{ color: 'var(--color-closed)' }}>{stats.closed}</div>
                  <div className="pd-stat-label">Closed</div>
                </div>
                <div className="pd-stat-divider" />
                <div className="pd-stat">
                  <div className="pd-stat-value">{stats.total}</div>
                  <div className="pd-stat-label">Total Issues</div>
                </div>
                <div className="pd-stat-divider" />
                <div className="pd-stat">
                  <div className="pd-stat-value">{stats.members}</div>
                  <div className="pd-stat-label">Members</div>
                </div>
              </>
            )}
          </div>

          {/* Description */}
          {project.description ? (
            <div className="pd-section">
              <div className="pd-section-label">Description</div>
              <p className="pd-description">{project.description}</p>
            </div>
          ) : (
            <div className="pd-section">
              <div className="pd-section-label">Description</div>
              <p className="pd-empty-text">No description provided.</p>
            </div>
          )}

          {/* Link */}
          {project.link && (
            <div className="pd-section">
              <div className="pd-section-label">Project Link</div>
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="pd-link"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                  <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                </svg>
                {safeDomain(project.link)}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" style={{ marginLeft: 'auto', opacity: 0.5 }}>
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                  <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </div>
          )}

          {/* Meta row */}
          <div className="pd-meta-row">
            {/* Creator */}
            <div className="pd-meta-item">
              <div className="pd-section-label">Created by</div>
              <div className="pd-creator">
                <div
                  className="pd-creator-avatar"
                  style={{ background: ROLE_COLORS[creatorRole] ?? '#6b7280' }}
                >
                  {creatorInitial}
                </div>
                <div>
                  <div className="pd-creator-name">{creatorName}</div>
                  {creatorRole && (
                    <div className="pd-creator-role" style={{ color: ROLE_COLORS[creatorRole] ?? '#6b7280' }}>
                      {creatorRole}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Date */}
            <div className="pd-meta-item">
              <div className="pd-section-label">Created on</div>
              <div className="pd-date">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                {formatDate(project.created_at)}
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="pd-footer">
          {canManage && (
            <button className="btn btn-secondary" onClick={onEdit}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Edit Details
            </button>
          )}
          <button
            className="btn btn-primary"
            style={{ marginLeft: canManage ? 0 : 'auto' }}
            onClick={() => { navigate(`/projects/${project.id}`); onClose(); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
            </svg>
            Open Project
          </button>
        </div>

      </div>
    </div>
  );
}
